import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { dispatch } from '../index';
import configData from '../../config';

// Maps frontend sortBy keys → Django ORM field names
const ORDERING_MAP = {
    id:                   'id',
    incident_date:        'incident_date',
    victim_name:          'victim_name',
    victim_grade:         'victim_grade',
    alleged_bully_name:   'alleged_bully_name',
    alleged_bully_grade:  'alleged_bully_grade',
    incident_location:    'incident_location',
    reporter_name:        'reporter_name',
    campus_label:         'campus__label',
    district_label:       'campus__parent__label',
};

const initialState = {
    employee: null,
    employeeLoading: false,
    employeeError: null,

    // Scope derived from employee campus
    scopeType: null,   // 'campus' | 'district' | 'central_office'
    scopeId:   null,

    // Dropdown options
    campuses:  [],     // school campuses for campus filter dropdown
    districts: [],     // district campuses for CO district filter dropdown

    // Paginated list (Dashboard)
    reports:        [],
    reportsLoading: false,
    reportsError:   null,
    totalCount:     0,

    // Full dataset for analytics charts (Reports page)
    allReports:        [],
    allReportsLoading: false,

    // Search / filter / sort / pagination
    keyword:      '',
    filterGrade:  '',
    filterCampus: '',
    filterDistrict: '',
    sortBy:       'incident_date',
    sortOrder:    'desc',
    page:         0,
    rowsPerPage:  25,

    selectedReport: null,
};

const slice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        // ── Employee ──────────────────────────────────────────────────────────
        setEmployeeLoading(state) {
            state.employeeLoading = true;
            state.employeeError = null;
        },
        setEmployeeSuccess(state, action) {
            state.employee = action.payload;
            state.employeeLoading = false;
        },
        setEmployeeError(state, action) {
            state.employeeError = action.payload;
            state.employeeLoading = false;
        },

        // ── Scope ─────────────────────────────────────────────────────────────
        setScopeSuccess(state, action) {
            state.scopeType = action.payload.scopeType;
            state.scopeId   = action.payload.scopeId;
        },

        // ── Dropdowns ─────────────────────────────────────────────────────────
        setCampusesSuccess(state, action) {
            state.campuses = action.payload;
        },
        clearCampuses(state) {
            state.campuses = [];
        },
        setDistrictsSuccess(state, action) {
            state.districts = action.payload;
        },

        // ── Paginated reports (Dashboard) ─────────────────────────────────────
        setReportsLoading(state) {
            state.reportsLoading = true;
            state.reportsError   = null;
        },
        setReportsSuccess(state, action) {
            state.reports        = action.payload;
            state.reportsLoading = false;
        },
        setReportsError(state, action) {
            state.reportsError   = action.payload;
            state.reportsLoading = false;
        },
        setTotalCount(state, action) {
            state.totalCount = action.payload;
        },

        // ── All reports for analytics (Reports page) ──────────────────────────
        setAllReportsLoading(state) {
            state.allReportsLoading = true;
        },
        setAllReportsSuccess(state, action) {
            state.allReports        = action.payload;
            state.allReportsLoading = false;
        },

        // ── Filters / sort / pagination ───────────────────────────────────────
        setKeyword(state, action) {
            state.keyword = action.payload;
            state.page    = 0;
        },
        setFilterGrade(state, action) {
            state.filterGrade = action.payload;
            state.page        = 0;
        },
        setFilterCampus(state, action) {
            state.filterCampus = action.payload;
            state.page         = 0;
        },
        setFilterDistrict(state, action) {
            state.filterDistrict = action.payload;
            state.filterCampus   = '';
            state.page           = 0;
        },
        setSortBy(state, action) {
            state.sortBy = action.payload;
        },
        setSortOrder(state, action) {
            state.sortOrder = action.payload;
        },
        setPage(state, action) {
            state.page = action.payload;
        },
        setRowsPerPage(state, action) {
            state.rowsPerPage = action.payload;
            state.page        = 0;
        },
        setSelectedReport(state, action) {
            state.selectedReport = action.payload;
        },

        // ── Reset ─────────────────────────────────────────────────────────────
        clearEmployee() {
            return { ...initialState };
        },
    },
});

export default slice.reducer;

export const {
    setScopeSuccess,
    setKeyword,
    setFilterGrade,
    setFilterCampus,
    setFilterDistrict,
    setSortBy,
    setSortOrder,
    setPage,
    setRowsPerPage,
    setSelectedReport,
} = slice.actions;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const authHeaders = () => ({ Authorization: 'Token ' + configData.TOKEN });

async function fetchAllPages(initialUrl) {
    let url = initialUrl;
    let all = [];
    while (url) {
        const response = await axios.get(url, { headers: authHeaders() });
        const data = response.data;
        if (data && data.results) {
            all = all.concat(data.results);
            url = data.next || null;
        } else {
            all = Array.isArray(data) ? data : [];
            url = null;
        }
    }
    return all;
}

/**
 * Build URLSearchParams for bullying-report queries.
 * scopeType / scopeId set the base scope; filterCampus / filterDistrict narrow it.
 */
function buildReportParams({ scopeType, scopeId, filterCampus, filterDistrict, keyword, grade, sortBy, sortOrder, page, rowsPerPage, dateFrom, dateTo }) {
    const p = new URLSearchParams();

    // Scope (most specific wins)
    if (filterCampus) {
        p.set('campus', filterCampus);
    } else if (filterDistrict && scopeType === 'central_office') {
        p.set('parent', filterDistrict);
    } else if (scopeType === 'campus') {
        p.set('campus', scopeId);
    } else if (scopeType === 'district') {
        p.set('parent', scopeId);
    } else if (scopeType === 'central_office') {
        p.set('grandparent', scopeId);
    }

    if (keyword)  p.set('search',    keyword);
    if (grade)    p.set('grade',     grade);
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo)   p.set('date_to',   dateTo);

    const field = ORDERING_MAP[sortBy] || 'incident_date';
    p.set('ordering', sortOrder === 'asc' ? field : `-${field}`);

    if (page !== undefined)       p.set('page',      page + 1);  // DRF is 1-indexed
    if (rowsPerPage !== undefined) p.set('page_size', rowsPerPage);

    return p;
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export function fetchEmployeeByEmail(email) {
    return async () => {
        dispatch(slice.actions.setEmployeeLoading());
        try {
            const url = `${configData.API_SERVER}employee?email=${encodeURIComponent(email)}`;
            const response = await axios.get(url, { headers: authHeaders() });
            const results = response.data.results || response.data || [];
            if (results.length > 0) {
                dispatch(slice.actions.setEmployeeSuccess(results[0]));
            } else {
                dispatch(slice.actions.setEmployeeError('No employee found with this email address.'));
            }
        } catch {
            dispatch(slice.actions.setEmployeeError('Failed to load employee information.'));
        }
    };
}

/** Fetch one page of reports for the Dashboard list. */
export function fetchReportsPage(params) {
    return async () => {
        dispatch(slice.actions.setReportsLoading());
        try {
            const query = buildReportParams(params);
            const url   = `${configData.API_SERVER}bullying-report?${query}`;
            const response = await axios.get(url, { headers: authHeaders() });
            dispatch(slice.actions.setReportsSuccess(response.data.results || []));
            dispatch(slice.actions.setTotalCount(response.data.count || 0));
        } catch {
            dispatch(slice.actions.setReportsError('Failed to load reports.'));
        }
    };
}

/** Fetch all reports (all pages) for analytics charts on the Reports page. */
export function fetchAllReportsForAnalytics({ scopeType, scopeId, dateFrom, dateTo }) {
    return async () => {
        dispatch(slice.actions.setAllReportsLoading());
        try {
            const p = new URLSearchParams();
            if (scopeType === 'campus')         p.set('campus',      scopeId);
            else if (scopeType === 'district')   p.set('parent',      scopeId);
            else if (scopeType === 'central_office') p.set('grandparent', scopeId);
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            p.set('ordering', '-incident_date');
            p.set('page_size', 500);

            const results = await fetchAllPages(`${configData.API_SERVER}bullying-report?${p}`);
            dispatch(slice.actions.setAllReportsSuccess(results));
        } catch {
            dispatch(slice.actions.setAllReportsSuccess([]));
        }
    };
}

/** Fetch district campuses (is_school=false) for Central Office admin's district filter dropdown. */
export function fetchDistrictsByParent(coId) {
    return async () => {
        try {
            const results = await fetchAllPages(`${configData.API_SERVER}campus?parent=${coId}&is_school=false`);
            dispatch(slice.actions.setDistrictsSuccess(results));
        } catch {
            dispatch(slice.actions.setDistrictsSuccess([]));
        }
    };
}

/** Fetch school campuses for a district admin's campus filter dropdown. */
export function fetchCampusesByParent(parentId) {
    return async () => {
        try {
            const results = await fetchAllPages(`${configData.API_SERVER}campus?parent=${parentId}`);
            dispatch(slice.actions.setCampusesSuccess(results));
        } catch {
            dispatch(slice.actions.setCampusesSuccess([]));
        }
    };
}

export function clearAdminState() {
    return () => {
        dispatch(slice.actions.clearEmployee());
    };
}

/** Build export URL with current filters — used by frontend to trigger backend download. */
export function buildExportUrl({ scopeType, scopeId, filterCampus, filterDistrict, keyword, grade, sortBy, sortOrder, dateFrom, dateTo, format }) {
    const p = buildReportParams({ scopeType, scopeId, filterCampus, filterDistrict, keyword, grade, sortBy, sortOrder, dateFrom, dateTo });
    p.set('format', format || 'xlsx');
    return `${configData.API_SERVER}bullying-report/export?${p}`;
}
